/**
 * useChannelOrders - Manages per-channel ordering state and reorder logic.
 */

import { useState, useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type { ChannelOrderState } from './types';

interface GroupedByChannel {
  [key: string]: { link_id: string; channel_id: string | null }[];
}

export function useChannelOrders(
  groupedByChannel: GroupedByChannel,
  setChangedAssetIds: (fn: (prev: Set<string>) => Set<string>) => void,
  setChangedChannelKeys: (fn: (prev: Set<string>) => Set<string>) => void,
) {
  const [channelOrders, setChannelOrders] = useState<Map<string, ChannelOrderState>>(new Map());

  const initializeChannelOrder = useCallback(
    (channelKey: string) => {
      if (channelOrders.has(channelKey)) return;
      const channelAssets = groupedByChannel[channelKey] || [];
      const order = channelAssets.map((a) => a.link_id);
      const isGlobal = channelKey === 'global';

      setChannelOrders((prev) => {
        const next = new Map(prev);
        next.set(channelKey, {
          channelId: isGlobal ? null : channelAssets[0]?.channel_id || null,
          originalOrder: order,
          currentOrder: order,
        });
        return next;
      });
    },
    [groupedByChannel, channelOrders]
  );

  const handleReorder = useCallback(
    (channelKey: string, oldIndex: number, newIndex: number) => {
      const channelAssets = groupedByChannel[channelKey] || [];

      // Gelber Punkt: Channel-scoped composite key (channelKey::link_id).
      // Beim arrayMove(oldIndex → newIndex) verschieben sich ALLE Bilder im
      // Index-Bereich [min, max] einen Slot, nicht nur das gezogene — daher
      // markieren wir den gesamten betroffenen Bereich, damit die UI dem
      // tatsächlichen Backend-Verhalten entspricht.
      const lo = Math.min(oldIndex, newIndex);
      const hi = Math.max(oldIndex, newIndex);
      setChangedAssetIds((prev) => {
        const next = new Set(prev);
        for (let i = lo; i <= hi; i++) {
          const linkId = channelAssets[i]?.link_id;
          if (linkId) next.add(`${channelKey}::${linkId}`);
        }
        return next;
      });
      setChangedChannelKeys((prev) => new Set(prev).add(channelKey));

      setChannelOrders((prev) => {
        const next = new Map(prev);
        const state = next.get(channelKey);
        if (!state) {
          const order = channelAssets.map((a) => a.link_id);
          const isGlobal = channelKey === 'global';
          next.set(channelKey, {
            channelId: isGlobal ? null : channelAssets[0]?.channel_id || null,
            originalOrder: order,
            currentOrder: arrayMove(order, oldIndex, newIndex),
          });
        } else {
          next.set(channelKey, {
            ...state,
            currentOrder: arrayMove(state.currentOrder, oldIndex, newIndex),
          });
        }
        return next;
      });
    },
    [groupedByChannel, setChangedAssetIds, setChangedChannelKeys]
  );

  return { channelOrders, setChannelOrders, initializeChannelOrder, handleReorder };
}
